<template>
  <div class="cat-page">
    <!-- Barra de anuncios -->
    <div class="cat-announce">Envíos a toda Colombia · Mínimo 6 unidades por pedido</div>

    <!-- Header sticky -->
    <header class="cat-header" :class="{ 'cat-header--shadow': headerShadow }">
      <div class="cat-container cat-header-inner">
        <div class="cat-brand" :class="{ 'cat-brand--link': productId }" @click="productId && goBack()">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="cat-logo" />
          <div v-else class="cat-logo cat-logo--ph"><i class="fas fa-tshirt"></i></div>
          <span class="cat-brand-name">{{ companyName || 'CATÁLOGO' }}</span>
        </div>
        <div class="cat-search-wrap">
          <i class="fas fa-search cat-search-icon"></i>
          <input
            v-model="search"
            type="text"
            class="cat-search"
            placeholder="Buscar producto o referencia..."
          />
          <button v-if="search" type="button" class="cat-search-clear" aria-label="Limpiar" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button
          v-if="categories.length"
          type="button"
          class="cat-menu-btn"
          aria-label="Ver categorías"
          @click="showCategoryMenu = true"
        >
          <i class="fas fa-bars"></i>
        </button>
        <button type="button" class="cat-cart-btn" aria-label="Ver carrito" @click="openCart">
          <i class="fas fa-shopping-bag"></i>
          <span v-if="unitsCount > 0" class="cat-cart-badge">{{ unitsCount > 99 ? '99+' : unitsCount }}</span>
        </button>
      </div>
    </header>

    <!-- Banners a todo el ancho de la pantalla (fuera del contenedor centrado) -->
    <section
      v-if="banners.length"
      v-show="!productId"
      class="cat-banner"
      @mouseenter="stopBannerTimer"
      @mouseleave="startBannerTimer"
      @touchstart.passive="onBannerTouchStart"
      @touchend="onBannerTouchEnd"
    >
      <div class="cat-banner-track">
        <picture
          v-for="(b, i) in banners"
          :key="'bn' + i"
          class="cat-banner-slide"
          :class="{ 'cat-banner-slide--active': i === bannerPos }"
        >
          <source v-if="b.mobile && b.desktop" media="(max-width: 750px)" :srcset="imageUrl(b.mobile)" />
          <img :src="imageUrl(b.desktop || b.mobile)" alt="Promoción" />
        </picture>
      </div>
      <template v-if="banners.length > 1">
        <button type="button" class="pgal-arrow pgal-arrow--prev" aria-label="Banner anterior" @click="bannerStep(-1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button type="button" class="pgal-arrow pgal-arrow--next" aria-label="Banner siguiente" @click="bannerStep(1)">
          <i class="fas fa-chevron-right"></i>
        </button>
        <div class="pcard-dots">
          <button
            v-for="(b, i) in banners"
            :key="'bd' + i"
            type="button"
            class="pcard-dot"
            :class="{ 'pcard-dot--active': i === bannerPos }"
            :aria-label="'Banner ' + (i + 1)"
            @click="goBanner(i)"
          ></button>
        </div>
      </template>
    </section>

    <!-- La grilla se oculta (no se destruye) al ver un producto: al volver queda igual y en la misma posición -->
    <main v-show="!productId" class="cat-container">

      <!-- Título de colección -->
      <div class="cat-title">
        <h1>MAYORISTAS TEAM ENSUEÑO</h1>
        <p>Atendido por <strong>{{ sellerName || '—' }}</strong> · Pedidos por WhatsApp</p>
      </div>

      <!-- Promociones vigentes (solo si hay alguna) -->
      <section v-if="activePromotions.length" class="cat-promos">
        <h2 class="cat-promos-title"><i class="fas fa-tags"></i> Promociones</h2>
        <div class="cat-promos-list">
          <button
            v-for="promo in activePromotions"
            :key="promo.id"
            type="button"
            class="cat-promo"
            :class="{ 'cat-promo--active': activePromotionId === promo.id }"
            @click="togglePromotion(promo.id)"
          >
            <span class="cat-promo-pct">-{{ formatPct(promo.discount_percent) }}%</span>
            <span class="cat-promo-body">
              <span class="cat-promo-name">{{ promo.name }}</span>
              <span v-if="promo.description" class="cat-promo-desc">{{ promo.description }}</span>
              <span class="cat-promo-meta">
                {{ promo.count }} producto{{ promo.count === 1 ? '' : 's' }}
                <template v-if="promo.ends_at"> · Hasta el {{ formatShortDate(promo.ends_at) }}</template>
              </span>
            </span>
            <span class="cat-promo-cta">{{ activePromotionId === promo.id ? 'Ver todo' : 'Ver' }}</span>
          </button>
        </div>
      </section>

      <!-- Toolbar -->
      <div ref="toolbar" class="cat-toolbar">
        <span class="cat-count">{{ sortedProducts.length }} producto{{ sortedProducts.length === 1 ? '' : 's' }}</span>
        <div class="cat-sort">
          <select v-model="sortBy" class="cat-sort-select" aria-label="Ordenar por">
            <option value="featured">Destacados</option>
            <option value="az">Alfabéticamente A–Z</option>
            <option value="za">Alfabéticamente Z–A</option>
            <option value="price_asc">Precio menor a mayor</option>
            <option value="price_desc">Precio mayor a menor</option>
            <option value="stock">Mayor stock</option>
          </select>
          <i class="fas fa-chevron-down cat-sort-chevron"></i>
        </div>
      </div>

      <!-- Chips de categorías (flechas indican que hay más hacia los lados) -->
      <div v-if="categories.length" class="cat-chips-wrap">
        <button
          v-show="chipsCanLeft"
          type="button"
          class="cat-chips-arrow cat-chips-arrow--prev"
          aria-label="Ver categorías anteriores"
          @click="scrollChips(-1)"
        ><span><i class="fas fa-chevron-left"></i></span></button>
        <div ref="chips" class="cat-chips" @wheel="onChipsWheel" @scroll.passive="updateChipsArrows">
          <button
            type="button"
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCategoryId === null }"
            @click="activeCategoryId = null"
          >Catálogo</button>
          <button
            v-for="c in categories"
            :key="c.id"
            type="button"
            class="cat-chip"
            :class="{ 'cat-chip--active': activeCategoryId === c.id }"
            @click="activeCategoryId = c.id"
          >{{ categoryLabel(c) }}</button>
        </div>
        <button
          v-show="chipsCanRight"
          type="button"
          class="cat-chips-arrow cat-chips-arrow--next"
          aria-label="Ver más categorías"
          @click="scrollChips(1)"
        ><span><i class="fas fa-chevron-right"></i></span></button>
      </div>

      <!-- Grilla -->
      <div v-if="loading" class="pgrid">
        <div v-for="i in 8" :key="'sk' + i" class="pcard-skeleton">
          <div class="pcard-skel-media"></div>
          <div class="pcard-skel-line" style="width: 40%"></div>
          <div class="pcard-skel-line" style="width: 85%"></div>
          <div class="pcard-skel-line" style="width: 25%"></div>
        </div>
      </div>

      <div v-else-if="sortedProducts.length === 0" class="cat-empty">
        <i class="fas fa-tshirt"></i>
        <p>No se encontraron productos</p>
        <a href="#" @click.prevent="clearFilters">Limpiar búsqueda</a>
      </div>

      <div v-else class="pgrid">
        <div
          v-for="p in sortedProducts"
          :key="p.id"
          class="pcard"
          @click="onCardClick(p)"
        >
          <div class="pcard-media" @mousemove="onZoomMove" @mouseleave="onZoomLeave">
            <transition name="slidefade" mode="out-in">
              <img
                v-if="currentSlideUrl(p)"
                :key="currentSlideUrl(p)"
                :src="currentSlideUrl(p)"
                :alt="p.name"
                loading="lazy"
                @error="onSlideError(p, $event)"
              />
              <i v-else class="fas fa-tshirt pcard-ph"></i>
            </transition>

            <!-- Carrusel: flechas y dots -->
            <template v-if="slideCount(p) > 1">
              <button
                type="button"
                class="pcard-arrow pcard-arrow--prev"
                aria-label="Foto anterior"
                @click.stop="slidePhoto(p, -1)"
              ><i class="fas fa-chevron-left"></i></button>
              <button
                type="button"
                class="pcard-arrow pcard-arrow--next"
                aria-label="Foto siguiente"
                @click.stop="slidePhoto(p, 1)"
              ><i class="fas fa-chevron-right"></i></button>
              <div class="pcard-dots">
                <button
                  v-for="(s, i) in aliveSlides(p)"
                  :key="s.i"
                  type="button"
                  class="pcard-dot"
                  :class="{ 'pcard-dot--active': i === activeSlidePos(p) }"
                  :aria-label="'Foto ' + (i + 1)"
                  @click.stop="goSlide(p, i)"
                ></button>
              </div>
            </template>

            <!-- Badges -->
            <span v-if="isSoldOut(p)" class="pcard-badge pcard-badge--out">Agotado</span>
            <span v-else-if="discountOf(p) > 0" class="pcard-badge pcard-badge--sale">-{{ formatPct(discountOf(p)) }}%</span>
          </div>
          <div class="pcard-info">
            <h3 class="pcard-name">{{ p.name }}</h3>
            <div v-if="wholesaleOf(p) > 0" class="pcard-price-row">
              <span class="price-label">Precio Mayor:</span>
              <span class="pcard-price">{{ formatCop(finalPrice(p, wholesaleOf(p))) }}</span>
              <span class="pill pill-cop">COP</span>
              <s v-if="discountOf(p) > 0" class="price-old">{{ formatCop(wholesaleOf(p)) }}</s>
              <span v-if="dollarRate > 0" class="pill pill-usd">USD {{ formatUsd(finalPrice(p, wholesaleOf(p))) }}</span>
            </div>
            <div v-if="retailOf(p) > 0" class="price-retail price-suggested">
              <span class="price-suggested-label">P.V. Sugerido</span>
              <span class="price-suggested-value">
                {{ formatCop(finalPrice(p, retailOf(p))) }}
                <s v-if="discountOf(p) > 0" class="price-old">{{ formatCop(retailOf(p)) }}</s>
              </span>
            </div>
            <p v-if="!wholesaleOf(p) && !retailOf(p)" class="price-retail">Precio a consultar</p>
            <p class="pcard-ref" v-if="p.ref">Ref. {{ p.ref }}</p>
          </div>
        </div>
      </div>
    </main>

    <!-- Detalle de producto (estilo tienda): galería + color/talla + descripción -->
    <main v-if="productId" class="cat-container pdp">
      <div class="pdp-nav">
        <button type="button" class="pdp-back" @click="goBack">
          <i class="fas fa-arrow-left"></i> Volver al catálogo
        </button>
        <span v-if="detailProduct?.categoria_nombre" class="pdp-crumb">
          <template v-if="detailProduct.categoria_padre_nombre">{{ detailProduct.categoria_padre_nombre }} / </template>{{ detailProduct.categoria_nombre }}
        </span>
      </div>

      <div v-if="loading" class="pdp-loading"><i class="fas fa-spinner fa-spin"></i></div>

      <div v-else-if="!detailProduct" class="cat-empty">
        <i class="fas fa-tshirt"></i>
        <p>Este producto ya no está disponible</p>
        <a href="#" @click.prevent="goBack">Volver al catálogo</a>
      </div>

      <div v-else class="pdp-grid">
        <!-- Galería -->
        <div class="pdp-media">
          <div
            class="pgal"
            :class="{ 'pgal--zoom': galZoom }"
            @touchstart.passive="onGalTouchStart"
            @touchmove="onGalTouchMove"
            @touchend="onGalTouchEnd"
            @mousemove="onZoomMove"
            @mouseleave="onZoomLeave"
          >
            <transition name="slidefade" mode="out-in">
              <img
                v-if="galImageUrl"
                :key="galImageUrl"
                :src="galImageUrl"
                :alt="detailProduct.name"
                @error="onGalSlideError"
              />
              <i v-else class="fas fa-tshirt pcard-ph"></i>
            </transition>
            <span v-if="isSoldOut(detailProduct)" class="pcard-badge pcard-badge--out">Agotado</span>
            <span v-else-if="discountOf(detailProduct) > 0" class="pcard-badge pcard-badge--sale">-{{ formatPct(discountOf(detailProduct)) }}%</span>
            <button
              v-if="galImageUrl"
              type="button"
              class="pgal-zoom"
              :aria-label="galZoom ? 'Quitar zoom' : 'Ampliar imagen'"
              @click.stop="toggleGalZoom"
            >
              <i :class="galZoom ? 'fas fa-search-minus' : 'fas fa-search-plus'"></i>
            </button>
            <template v-if="galSlides.length > 1">
              <button type="button" class="pgal-arrow pgal-arrow--prev" aria-label="Foto anterior" @click="galStep(-1)">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button type="button" class="pgal-arrow pgal-arrow--next" aria-label="Foto siguiente" @click="galStep(1)">
                <i class="fas fa-chevron-right"></i>
              </button>
              <span class="pgal-counter">{{ galPos + 1 }} / {{ galSlides.length }}</span>
            </template>
          </div>
          <div v-if="galSlides.length > 1" class="pgal-thumbs">
            <button
              v-for="(s, i) in galSlides"
              :key="s.url"
              type="button"
              class="pgal-thumb"
              :class="{ 'pgal-thumb--active': i === galPos }"
              :aria-label="'Foto ' + (i + 1)"
              @click="galPos = i"
            >
              <img :src="s.url" alt="" loading="lazy" />
            </button>
          </div>
        </div>

        <!-- Información y compra -->
        <div class="pdp-info">
          <p v-if="detailProduct.ref" class="pdp-ref">Ref. {{ detailProduct.ref }}</p>
          <h1 class="pdp-title">{{ detailProduct.name }}</h1>

          <div v-if="pdpWholesale > 0 || pdpRetail > 0" class="vprice">
            <div v-if="pdpWholesale > 0" class="vprice-main">
              <span class="vprice-label">Precio Mayor</span>
              <div class="vprice-row">
                <span class="vprice-value">{{ formatCop(finalPrice(detailProduct, pdpWholesale)) }}</span>
                <span class="vprice-cur">COP</span>
                <s v-if="discountOf(detailProduct) > 0" class="vprice-old">{{ formatCop(pdpWholesale) }}</s>
                <span v-if="discountOf(detailProduct) > 0" class="pill pill-sale">-{{ formatPct(discountOf(detailProduct)) }}%</span>
              </div>
              <span v-if="dollarRate > 0" class="pill pill-usd vprice-usd">USD {{ formatUsd(finalPrice(detailProduct, pdpWholesale)) }}</span>
            </div>
            <div v-if="pdpRetail > 0" class="vprice-retail">
              <span class="vprice-label vprice-label--stack">P.V. Sugerido</span>
              <span class="vprice-retail-value">
                {{ formatCop(finalPrice(detailProduct, pdpRetail)) }}
                <s v-if="discountOf(detailProduct) > 0" class="vprice-old">{{ formatCop(pdpRetail) }}</s>
              </span>
            </div>
          </div>
          <p v-else class="price-retail">Precio a consultar</p>

          <!-- Color -->
          <div v-if="pdpColors.length" class="pdp-opt">
            <div class="pdp-opt-label">
              Color: <strong>{{ pdpSelectedColor ? pdpSelectedColor.name : 'Selecciona un color' }}</strong>
            </div>
            <div class="pdp-swatches">
              <button
                v-for="c in pdpColors"
                :key="c.key"
                type="button"
                class="pdp-swatch"
                :class="{
                  'pdp-swatch--active': pdpColorKey === c.key,
                  'pdp-swatch--out': c.soldOut,
                  'pdp-swatch--text': !c.image && !c.hex,
                }"
                :title="c.name + (c.soldOut ? ' (agotado)' : '')"
                @click="selectColor(c.key)"
              >
                <span v-if="c.image" class="pdp-swatch-img"><img :src="c.image" alt="" loading="lazy" /></span>
                <span v-else-if="c.hex" class="pdp-swatch-dot" :style="{ background: c.hex }"></span>
                <span class="pdp-swatch-name">{{ c.name }}</span>
              </button>
            </div>
          </div>

          <!-- Talla (las del color elegido) -->
          <div v-if="pdpHasSizes" class="pdp-opt">
            <div class="pdp-opt-label">
              Talla: <strong>{{ pdpVariant ? pdpVariant.size : 'Selecciona una talla' }}</strong>
            </div>
            <p v-if="pdpColors.length && !pdpColorKey" class="pdp-hint">Primero elige un color.</p>
            <div v-else class="pdp-sizes">
              <button
                v-for="v in pdpSizes"
                :key="v.id"
                type="button"
                class="pdp-size"
                :class="{ 'pdp-size--active': pdpVariant && pdpVariant.id === v.id, 'pdp-size--out': variantSoldOut(v) }"
                :disabled="variantSoldOut(v)"
                :title="variantSoldOut(v) ? 'Agotada' : 'Disponibles: ' + v.inventory_quantity"
                @click="selectSize(v)"
              >{{ v.size }}</button>
            </div>
          </div>

          <!-- Cantidad + agregar -->
          <div class="pdp-buy">
            <p v-if="pdpRow" class="pdp-stock">
              <template v-if="pdpRow.unlimited">Disponible</template>
              <template v-else-if="pdpMax > 0">Disponibles: {{ pdpMax }}</template>
              <template v-else>Ya agregaste todas las unidades disponibles</template>
              <span v-if="pdpInCart > 0" class="pdp-incart"> · {{ pdpInCart }} en tu carrito</span>
            </p>
            <div class="pdp-buy-row">
              <div class="stepper">
                <button type="button" class="stepper-btn" :disabled="pdpQty <= 1" @click="pdpQty--">−</button>
                <input
                  class="stepper-input"
                  type="text"
                  inputmode="numeric"
                  :value="pdpQty"
                  @input="pdpQtyInput"
                />
                <button type="button" class="stepper-btn" :disabled="!pdpRow || pdpQty >= pdpMax" @click="pdpQty++">+</button>
              </div>
              <button
                type="button"
                class="btn--dark pdp-add"
                :disabled="!pdpRow || pdpMax <= 0"
                @click="addFromDetail"
              >
                <i class="fas fa-cart-plus"></i>
                {{ pdpAddLabel }}
              </button>
            </div>
            <p class="pdp-hint">Mínimo {{ minUnits }} unidades por pedido (puedes combinar productos, tallas y colores).</p>
          </div>

          <!-- Descripción y detalles -->
          <div v-if="detailProduct.description" class="pdp-section">
            <h2 class="pdp-section-title">Descripción</h2>
            <p class="pdp-desc">{{ detailProduct.description }}</p>
          </div>
          <div class="pdp-section">
            <h2 class="pdp-section-title">Detalles</h2>
            <dl class="pdp-specs">
              <template v-if="detailProduct.ref"><dt>Referencia</dt><dd>{{ detailProduct.ref }}</dd></template>
              <template v-if="detailProduct.categoria_nombre"><dt>Categoría</dt><dd>{{ detailProduct.categoria_nombre }}</dd></template>
              <template v-if="pdpColors.length"><dt>Colores</dt><dd>{{ pdpColors.map((c) => c.name).join(', ') }}</dd></template>
              <template v-if="pdpAllSizes.length"><dt>Tallas</dt><dd>{{ pdpAllSizes.join(', ') }}</dd></template>
              <template v-if="detailProduct.promotion"><dt>Promoción</dt><dd>{{ detailProduct.promotion.name }}<template v-if="detailProduct.promotion.ends_at"> · hasta el {{ formatShortDate(detailProduct.promotion.ends_at) }}</template></dd></template>
            </dl>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="cat-footer">
      <img v-if="logoUrl" :src="logoUrl" alt="" class="cat-footer-logo" />
      <div v-else class="cat-footer-logo cat-logo--ph"><i class="fas fa-tshirt"></i></div>
      <p class="cat-footer-brand">{{ companyName || 'CATÁLOGO' }}</p>
      <p class="cat-footer-tag">Catálogo mayorista · Pedidos por WhatsApp</p>
      <p class="cat-footer-copy">© {{ new Date().getFullYear() }} {{ companyName || '' }}. Todos los derechos reservados.</p>
    </footer>

    <!-- Menú de categorías: cubre todo el catálogo -->
    <transition name="fade">
      <div v-if="showCategoryMenu" class="catmenu">
        <div class="catmenu-head">
          <span class="catmenu-title">Categorías</span>
          <button type="button" class="vmodal-close" aria-label="Cerrar" @click="showCategoryMenu = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="catmenu-body">
          <button
            type="button"
            class="catmenu-item"
            :class="{ 'catmenu-item--active': activeCategoryId === null }"
            @click="selectCategory(null)"
          >
            <span>Todo el catálogo</span>
            <span class="catmenu-count">{{ products.length }}</span>
          </button>
          <div v-for="g in categoryGroups" :key="g.name || '_'" class="catmenu-group">
            <div v-if="g.name" class="catmenu-group-title">{{ g.name }}</div>
            <button
              v-for="c in g.items"
              :key="c.id"
              type="button"
              class="catmenu-item"
              :class="{ 'catmenu-item--active': activeCategoryId === c.id }"
              @click="selectCategory(c.id)"
            >
              <span>{{ c.nombre }}</span>
              <span class="catmenu-count">{{ categoryCount(c.id) }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Drawer del carrito -->
    <transition name="fade">
      <div v-if="showCart" class="drawer-overlay" @click.self="closeCart">
        <aside class="drawer">
          <div class="drawer-head">
            <span class="drawer-title">Tu carrito <span class="drawer-count">({{ unitsCount }})</span></span>
            <button type="button" class="vmodal-close" aria-label="Cerrar" @click="closeCart">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div v-if="cart.length === 0" class="drawer-empty">
            <i class="fas fa-shopping-bag"></i>
            <p>Tu carrito está vacío</p>
            <button type="button" class="btn--dark" @click="closeCart">Continuar comprando</button>
          </div>

          <template v-else>
            <div class="drawer-lines">
              <div v-for="line in cart" :key="line.key" class="dline">
                <div class="dline-thumb">
                  <img v-if="line.image" :src="line.image" alt="" />
                  <i v-else class="fas fa-tshirt"></i>
                </div>
                <div class="dline-info">
                  <p class="dline-name">{{ line.name }}</p>
                  <span v-if="line.variantLabel" class="dline-variant">{{ line.variantLabel }}</span>
                  <p class="dline-unit">{{ formatCop(line.price) }} c/u</p>
                  <div class="dline-controls">
                    <div class="stepper stepper--sm">
                      <button
                        type="button"
                        class="stepper-btn"
                        @click="changeQty(line, -1)"
                      >−</button>
                      <input
                        class="stepper-input"
                        type="text"
                        inputmode="numeric"
                        :value="line.qty"
                        @input="lineInput(line, $event)"
                      />
                      <button
                        type="button"
                        class="stepper-btn"
                        :disabled="line.maxQty ? line.qty >= line.maxQty : false"
                        @click="changeQty(line, 1)"
                      >+</button>
                    </div>
                    <a href="#" class="dline-remove" @click.prevent="removeLine(line)">Eliminar</a>
                  </div>
                </div>
                <span class="dline-total">{{ formatCop(line.price * line.qty) }}</span>
              </div>
            </div>

            <div class="drawer-foot">
              <!-- Mínimo de unidades -->
              <div class="minbar">
                <div class="minbar-track"><div class="minbar-fill" :style="{ width: minPercent + '%' }"></div></div>
                <p class="minbar-text" :class="{ 'minbar-text--ok': minReached }">
                  {{ unitsCount }}/6 unidades · mínimo por pedido
                </p>
              </div>

              <div class="drawer-subtotal">
                <span class="drawer-subtotal-label">Subtotal</span>
                <span class="drawer-subtotal-value">
                  {{ formatCop(subtotal) }}
                  <span v-if="dollarRate > 0" class="pill pill-usd">USD {{ formatUsd(subtotal) }}</span>
                </span>
              </div>
              <div class="drawer-shipping">
                <span class="drawer-subtotal-label">Envío</span>
                <span class="drawer-shipping-value">Se cotiza al despachar</span>
              </div>
              <p class="drawer-note">
                <i class="fas fa-truck"></i> El valor del envío se cotiza al momento del despacho.
              </p>
              <p class="drawer-note">
                <i class="fab fa-whatsapp"></i> El pedido se envía y confirma por WhatsApp.
              </p>

              <div class="drawer-form">
                <input v-model="form.name" type="text" class="field" placeholder="Nombre completo *" />
                <input v-model="form.country" type="text" class="field" placeholder="País *" />
                <input v-model="form.city" type="text" class="field" placeholder="Ciudad *" />
                <input v-model="form.address" type="text" class="field" placeholder="Dirección *" />
                <p v-if="formError" class="drawer-error">{{ formError }}</p>
                <button
                  type="button"
                  class="btn-wa"
                  :disabled="sending"
                  @click="submitOrder"
                >
                  <i v-if="sending" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fab fa-whatsapp"></i>
                  {{ sending ? 'Enviando…' : 'Enviar pedido por WhatsApp' }}
                </button>
              </div>
            </div>
          </template>
        </aside>
      </div>
    </transition>

    <!-- Botón flotante del carrito: aparece después de agregar algo -->
    <transition name="fab">
      <button
        v-if="cart.length > 0 && !showCart"
        type="button"
        class="cat-fab"
        :class="{ 'cat-fab--bump': fabBump }"
        aria-label="Ver carrito"
        @click="openCart"
      >
        <i class="fas fa-shopping-cart"></i>
        <span class="cat-fab-badge">{{ unitsCount > 99 ? '99+' : unitsCount }}</span>
      </button>
    </transition>
  </div>
</template>

<script>
import catalogService from '@/services/catalog.service';

const MIN_UNITS = 6;
const copFormatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 });

export default {
  name: 'PublicCatalog',
  data() {
    return {
      token: '',
      sellerName: '',
      sellerPhone: '',
      companyName: '',
      logoUrl: '',
      dollarRate: 0,
      // Banners de promoción: [{ desktop, mobile }] con rutas relativas al storage
      banners: [],
      bannerPos: 0,
      bannerTimer: null,
      bannerTouchX: null,
      products: [],
      categories: [],
      activeCategoryId: null,
      showCategoryMenu: false,
      activePromotionId: null,
      chipsCanLeft: false,
      chipsCanRight: false,
      fabBump: false,
      loading: true,
      search: '',
      sortBy: 'featured',
      headerShadow: false,
      cart: [],
      showCart: false,
      minUnits: MIN_UNITS,
      // Detalle de producto: color (clave) y variante elegidos, cantidad y galería
      pdpColorKey: null,
      pdpVariantId: null,
      pdpQty: 1,
      galPos: 0,
      galZoom: false,
      galDead: [],
      galTouchX: null,
      // Posición del scroll del catálogo para restaurarla al volver del detalle
      catalogScrollY: 0,
      cameFromCatalog: false,
      sending: false,
      formError: '',
      // País y ciudad como texto libre para admitir clientes internacionales
      form: { name: '', country: 'Colombia', city: '', address: '' },
    };
  },
  computed: {
    unitsCount() {
      return this.cart.reduce((s, l) => s + l.qty, 0);
    },
    // Promociones vigentes a partir de los productos que traen una
    activePromotions() {
      const map = new Map();
      this.products.forEach((p) => {
        if (!p.promotion) return;
        const promo = map.get(p.promotion.id) || { ...p.promotion, count: 0 };
        promo.count += 1;
        map.set(promo.id, promo);
      });
      return [...map.values()].sort((a, b) => b.discount_percent - a.discount_percent);
    },
    // Categorías agrupadas por su padre para el menú (las sin padre van primero)
    categoryGroups() {
      const groups = new Map([[null, []]]);
      this.categories.forEach((c) => {
        const key = c.padre_nombre || null;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
      });
      return [...groups.entries()]
        .filter(([, items]) => items.length)
        .map(([name, items]) => ({ name, items }));
    },
    filteredProducts() {
      const q = this.normalize(this.search);
      return this.products.filter((p) => {
        // Los agotados se muestran igual (con etiqueta "Agotado"), no se ocultan
        if (q && !this.normalize(`${p.name} ${p.ref || ''}`).includes(q)) return false;
        if (this.activeCategoryId !== null
          && p.category_id !== this.activeCategoryId
          && p.categoria_padre_id !== this.activeCategoryId) return false;
        if (this.activePromotionId !== null && p.promotion?.id !== this.activePromotionId) return false;
        return true;
      });
    },
    sortedProducts() {
      const list = [...this.filteredProducts];
      switch (this.sortBy) {
        case 'az': list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'es')); break;
        case 'za': list.sort((a, b) => String(b.name).localeCompare(String(a.name), 'es')); break;
        case 'price_asc': list.sort((a, b) => this.sortPrice(a) - this.sortPrice(b)); break;
        case 'price_desc': list.sort((a, b) => this.sortPrice(b) - this.sortPrice(a)); break;
        case 'stock': list.sort((a, b) => this.stockCount(b) - this.stockCount(a)); break;
        default: break;
      }
      return list;
    },
    subtotal() {
      return this.cart.reduce((s, l) => s + l.price * l.qty, 0);
    },
    minReached() {
      return this.unitsCount >= MIN_UNITS;
    },
    minPercent() {
      return Math.min(100, Math.round((this.unitsCount / MIN_UNITS) * 100));
    },
    // ===== Detalle de producto =====
    productId() {
      return this.$route.params.productId || null;
    },
    detailProduct() {
      if (!this.productId) return null;
      return this.products.find((p) => String(p.id) === String(this.productId)) || null;
    },
    pdpVariants() {
      const p = this.detailProduct;
      if (!p || !p.has_variants || !p.variants?.length) return [];
      return [...p.variants].sort(this.compareSizes);
    },
    pdpHasColors() {
      return this.pdpVariants.some((v) => v.color);
    },
    pdpHasSizes() {
      return this.pdpVariants.some((v) => v.size);
    },
    // Colores únicos en el orden en que aparecen; agotado si ninguna talla tiene stock
    pdpColors() {
      if (!this.pdpHasColors) return [];
      const map = new Map();
      this.pdpVariants.forEach((v) => {
        const key = this.colorKey(v);
        const c = map.get(key) || { key, name: v.color || 'Sin color', hex: v.color_hex, image: null, soldOut: true };
        if (!c.image && v.image) c.image = v.image;
        if (!this.variantSoldOut(v)) c.soldOut = false;
        map.set(key, c);
      });
      return [...map.values()];
    },
    pdpSelectedColor() {
      return this.pdpColors.find((c) => c.key === this.pdpColorKey) || null;
    },
    // Tallas del color elegido (o todas si el producto no maneja color)
    pdpSizes() {
      if (!this.pdpHasColors) return this.pdpVariants;
      if (this.pdpColorKey === null) return [];
      return this.pdpVariants.filter((v) => this.colorKey(v) === this.pdpColorKey);
    },
    pdpAllSizes() {
      return [...new Set(this.pdpVariants.map((v) => v.size).filter(Boolean))];
    },
    pdpVariant() {
      return this.pdpSizes.find((v) => v.id === this.pdpVariantId) || null;
    },
    // Lo que se agrega al carrito: la variante elegida o, sin variantes, el producto
    pdpRow() {
      const p = this.detailProduct;
      if (!p) return null;
      if (this.pdpVariants.length) return this.pdpVariant;
      return {
        id: '_single',
        single: true,
        price: this.retailOf(p),
        wholesale_price: this.wholesaleOf(p),
        inventory_quantity: p.manage_stock ? parseInt(p.quantity) || 0 : null,
        unlimited: !p.manage_stock,
      };
    },
    pdpInCart() {
      if (!this.detailProduct || !this.pdpRow) return 0;
      const line = this.cart.find((l) => l.key === this.lineKey(this.detailProduct, this.pdpRow));
      return line ? line.qty : 0;
    },
    // Unidades que aún se pueden agregar (stock menos lo que ya está en el carrito)
    pdpMax() {
      const row = this.pdpRow;
      if (!row) return 0;
      if (row.unlimited) return Infinity;
      return Math.max(0, (parseInt(row.inventory_quantity) || 0) - this.pdpInCart);
    },
    // Precios: los de la variante elegida si los tiene, si no los del producto
    pdpWholesale() {
      const v = this.pdpVariant;
      return (v && parseFloat(v.wholesale_price)) || this.wholesaleOf(this.detailProduct);
    },
    pdpRetail() {
      const v = this.pdpVariant;
      return (v && parseFloat(v.price)) || this.retailOf(this.detailProduct);
    },
    pdpAddLabel() {
      if (!this.detailProduct) return '';
      if (this.isSoldOut(this.detailProduct)) return 'Agotado';
      if (!this.pdpRow) {
        return this.pdpHasColors && this.pdpColorKey === null ? 'Selecciona un color' : 'Selecciona una talla';
      }
      return 'Agregar al carrito';
    },
    // Galería: fotos del producto + fotos de las variantes (sin repetir)
    galSlides() {
      const p = this.detailProduct;
      if (!p) return [];
      const urls = [...this.allSlides(p), ...this.pdpVariants.map((v) => v.image).filter(Boolean)];
      return [...new Set(urls)]
        .filter((url) => !this.galDead.includes(url))
        .map((url) => ({ url }));
    },
    galImageUrl() {
      return this.galSlides[this.galPos]?.url || null;
    },
  },
  watch: {
    showCart(open) { this.lockBody(open || this.showCategoryMenu); },
    showCategoryMenu(open) { this.lockBody(open || this.showCart); },
    // Al cambiar de foto se quita el zoom
    galPos() { this.galZoom = false; },
    // Entrar/salir del detalle (también con el botón atrás del navegador)
    productId(id, prev) {
      if (id) {
        this.resetDetail();
        window.scrollTo(0, 0);
      } else if (prev) {
        this.$nextTick(() => window.scrollTo(0, this.catalogScrollY));
      }
    },
    // Si el producto llega después (enlace directo), se preselecciona al terminar de cargar
    detailProduct(p, prev) {
      if (p && !prev) this.resetDetail();
    },
    // Buscar desde el detalle lleva de vuelta a la grilla
    search(q) {
      if (q && this.productId) this.goBack();
    },
  },
  methods: {
    normalize(val) {
      return String(val || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    formatCop(val) {
      return copFormatter.format(parseFloat(val) || 0);
    },
    formatUsd(val) {
      if (!this.dollarRate) return '';
      return '$' + ((parseFloat(val) || 0) / this.dollarRate)
        .toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatStock(n) {
      return n >= 1000 ? '999+' : String(Math.round(n));
    },
    clearFilters() {
      this.search = '';
      this.activeCategoryId = null;
      this.activePromotionId = null;
    },
    togglePromotion(id) {
      this.activePromotionId = this.activePromotionId === id ? null : id;
    },
    formatShortDate(value) {
      const [y, m, d] = String(value).substring(0, 10).split('-');
      return `${d}/${m}/${y}`;
    },
    categoryCount(id) {
      return this.products.filter((p) => p.category_id === id || p.categoria_padre_id === id).length;
    },
    async selectCategory(id) {
      this.activeCategoryId = id;
      this.showCategoryMenu = false;
      // Desde el detalle se vuelve primero a la grilla
      if (this.productId) {
        this.goBack();
        await new Promise((resolve) => {
          const stop = this.$watch('productId', () => { stop(); setTimeout(resolve, 0); });
        });
      }
      this.$nextTick(() => {
        const el = this.$refs.toolbar;
        if (!el) return;
        const headerH = this.$el.querySelector('.cat-header')?.offsetHeight || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - headerH - 8;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      });
    },
    categoryLabel(c) {
      return c.padre_nombre ? `${c.padre_nombre} > ${c.nombre}` : c.nombre;
    },
    isSoldOut(p) {
      if (!p.manage_stock) return false;
      if (p.has_variants && p.variants && p.variants.length > 0) {
        return p.variants.every((v) => (parseInt(v.inventory_quantity) || 0) <= 0);
      }
      return (parseInt(p.quantity) || 0) <= 0;
    },
    variantSoldOut(v) {
      return (parseInt(v.inventory_quantity) || 0) <= 0;
    },
    // Tarifa al mayor: la definida a nivel de variante o, si no, la del producto
    variantUnitPrice(v) {
      return parseFloat(v.wholesale_price || v.price) || 0;
    },
    // ===== Precios =====
    // Precio del producto; si es 0 se toma el de la primera variante que tenga precio
    basePrice(p, productKey, variantKey) {
      if (!p) return 0;
      const own = parseFloat(p[productKey]) || 0;
      if (own > 0) return own;
      const v = (p.variants || []).find((x) => (parseFloat(x[variantKey]) || 0) > 0);
      return v ? parseFloat(v[variantKey]) : 0;
    },
    wholesaleOf(p) {
      return this.basePrice(p, 'wholesale_price', 'wholesale_price');
    },
    retailOf(p) {
      return this.basePrice(p, 'retail_price', 'price');
    },
    sortPrice(p) {
      return this.finalPrice(p, this.wholesaleOf(p) || this.retailOf(p));
    },
    discountOf(p) {
      const d = parseFloat(p?.discount_percent) || 0;
      return Math.min(100, Math.max(0, d));
    },
    finalPrice(p, price) {
      const d = this.discountOf(p);
      return d > 0 ? Math.round(price * (1 - d / 100)) : price;
    },
    formatPct(n) {
      return String(Math.round(n * 100) / 100).replace('.', ',');
    },
    imageUrl(path) {
      const base = (process.env.VUE_APP_API_IMAGE_URL || '').replace(/\/?$/, '/');
      return base + String(path).replace(/^\/+/, '');
    },
    // ===== Zoom al pasar el mouse: la imagen se amplía siguiendo el cursor =====
    onZoomMove(e) {
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
    },
    onZoomLeave(e) {
      e.currentTarget.style.setProperty('--zx', '50%');
      e.currentTarget.style.setProperty('--zy', '50%');
    },
    // ===== Banners =====
    bannerStep(dir) {
      const n = this.banners.length;
      if (n < 2) return;
      this.bannerPos = (this.bannerPos + dir + n) % n;
      this.startBannerTimer();
    },
    goBanner(i) {
      this.bannerPos = i;
      this.startBannerTimer();
    },
    startBannerTimer() {
      this.stopBannerTimer();
      if (this.banners.length < 2) return;
      this.bannerTimer = setInterval(() => {
        this.bannerPos = (this.bannerPos + 1) % this.banners.length;
      }, 5000);
    },
    stopBannerTimer() {
      if (this.bannerTimer) clearInterval(this.bannerTimer);
      this.bannerTimer = null;
    },
    onBannerTouchStart(e) {
      this.bannerTouchX = e.changedTouches[0].clientX;
    },
    onBannerTouchEnd(e) {
      if (this.bannerTouchX === null) return;
      const dx = e.changedTouches[0].clientX - this.bannerTouchX;
      this.bannerTouchX = null;
      if (Math.abs(dx) > 40) this.bannerStep(dx < 0 ? 1 : -1);
    },
    // ===== Galería del detalle =====
    galStep(dir) {
      const n = this.galSlides.length;
      if (n < 2) return;
      this.galPos = (this.galPos + dir + n) % n;
    },
    onGalSlideError() {
      const s = this.galSlides[this.galPos];
      if (!s) return;
      this.galDead = [...this.galDead, s.url];
      this.galPos = Math.min(this.galPos, Math.max(0, this.galSlides.length - 1));
    },
    // Lleva la galería a la foto indicada (si está en la galería)
    galShow(url) {
      const i = url ? this.galSlides.findIndex((s) => s.url === url) : -1;
      if (i >= 0) this.galPos = i;
    },
    onGalTouchStart(e) {
      this.galTouchX = e.changedTouches[0].clientX;
    },
    // Zoom con la lupa (pensado para táctil, donde no hay hover)
    toggleGalZoom(e) {
      this.galZoom = !this.galZoom;
      const el = e.currentTarget.closest('.pgal');
      el.style.setProperty('--zx', '50%');
      el.style.setProperty('--zy', '50%');
    },
    // Con zoom activo, arrastrar el dedo recorre la imagen en vez de cambiar de foto
    onGalTouchMove(e) {
      if (!this.galZoom) return;
      e.preventDefault();
      const t = e.touches[0];
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const clamp = (v) => Math.min(100, Math.max(0, v));
      el.style.setProperty('--zx', `${clamp(((t.clientX - r.left) / r.width) * 100)}%`);
      el.style.setProperty('--zy', `${clamp(((t.clientY - r.top) / r.height) * 100)}%`);
    },
    onGalTouchEnd(e) {
      if (this.galZoom) { this.galTouchX = null; return; }
      if (this.galTouchX === null) return;
      const dx = e.changedTouches[0].clientX - this.galTouchX;
      this.galTouchX = null;
      if (Math.abs(dx) > 40) this.galStep(dx < 0 ? 1 : -1);
    },
    // Rueda vertical del mouse -> scroll horizontal de los chips
    onChipsWheel(e) {
      const el = e.currentTarget;
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    },
    // Muestra las flechas solo hacia el lado donde quedan categorías por ver
    updateChipsArrows() {
      const el = this.$refs.chips;
      if (!el) return;
      this.chipsCanLeft = el.scrollLeft > 2;
      this.chipsCanRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    },
    scrollChips(dir) {
      const el = this.$refs.chips;
      if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
    },
    stockCount(p) {
      if (p.has_variants && p.variants && p.variants.length > 0) {
        return p.variants.reduce((s, v) => s + (parseInt(v.inventory_quantity) || 0), 0);
      }
      return parseInt(p.quantity) || 0;
    },
    stockLabel(n) {
      return this.formatStock(n);
    },
    // ===== Carrusel de fotos =====
    allSlides(p) {
      const list = [];
      if (p.image_url) list.push(p.image_url);
      (p.photos || []).forEach((url) => list.push(url));
      return list;
    },
    aliveSlides(p) {
      return this.allSlides(p)
        .map((url, i) => ({ url, i }))
        .filter((s) => !(p._dead || []).includes(s.i));
    },
    slideCount(p) {
      return this.aliveSlides(p).length;
    },
    activeSlidePos(p) {
      return Math.min(p._slide || 0, this.aliveSlides(p).length - 1);
    },
    currentSlideUrl(p) {
      const alive = this.aliveSlides(p);
      if (!alive.length) return null;
      return alive[this.activeSlidePos(p)].url;
    },
    slidePhoto(p, dir) {
      const n = this.aliveSlides(p).length;
      if (n < 2) return;
      p._slide = (this.activeSlidePos(p) + dir + n) % n;
    },
    goSlide(p, pos) {
      p._slide = pos;
    },
    onSlideError(p) {
      const alive = this.aliveSlides(p);
      if (!alive.length) return;
      const pos = this.activeSlidePos(p);
      p._dead = [...(p._dead || []), alive[pos].i];
      p._slide = Math.min(p._slide || 0, Math.max(0, this.aliveSlides(p).length - 1));
    },
    // ===== Detalle de producto =====
    onCardClick(p) {
      this.catalogScrollY = window.scrollY;
      this.cameFromCatalog = true;
      this.$router.push({ name: 'Public Catalog Product', params: { token: this.token, productId: p.id } });
    },
    // Vuelve a la grilla: con historial se usa "atrás" para no duplicar entradas
    goBack() {
      if (this.cameFromCatalog) {
        this.cameFromCatalog = false;
        this.$router.back();
      } else {
        this.$router.push({ name: 'Public Catalog', params: { token: this.token } });
      }
    },
    colorKey(v) {
      if (v.color_id) return `c${v.color_id}`;
      return v.color ? `n${v.color}` : '_none';
    },
    // Tallas numéricas de menor a mayor; las de letras en el orden habitual
    compareSizes(a, b) {
      const order = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL'];
      const sa = String(a.size || '').trim().toUpperCase();
      const sb = String(b.size || '').trim().toUpperCase();
      const na = parseFloat(sa.replace(',', '.'));
      const nb = parseFloat(sb.replace(',', '.'));
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      const ia = order.indexOf(sa);
      const ib = order.indexOf(sb);
      if (ia >= 0 && ib >= 0) return ia - ib;
      return sa.localeCompare(sb, 'es', { numeric: true });
    },
    // Estado inicial del detalle: primer color con stock y su talla si es la única
    resetDetail() {
      this.galDead = [];
      this.galZoom = false;
      this.pdpQty = 1;
      this.pdpVariantId = null;
      this.pdpColorKey = null;
      const p = this.detailProduct;
      this.galPos = p ? Math.max(0, this.activeSlidePos(p)) : 0;
      if (!p) return;
      if (this.pdpHasColors) {
        const first = this.pdpColors.find((c) => !c.soldOut) || this.pdpColors[0];
        if (first) this.selectColor(first.key, false);
      } else {
        this.autoPickSize();
      }
    },
    // Con una sola talla disponible (o sin tallas) se elige sola
    autoPickSize() {
      const available = this.pdpSizes.filter((v) => !this.variantSoldOut(v));
      if (available.length === 1 && (this.pdpSizes.length === 1 || !this.pdpHasSizes)) {
        this.pdpVariantId = available[0].id;
      }
    },
    selectColor(key, moveGallery = true) {
      const prevSize = this.pdpVariant?.size;
      this.pdpColorKey = key;
      // Se conserva la misma talla en el nuevo color si está disponible
      const same = this.pdpSizes.find((v) => v.size === prevSize && !this.variantSoldOut(v));
      this.pdpVariantId = same ? same.id : null;
      if (!same) this.autoPickSize();
      this.pdpQty = 1;
      if (moveGallery) {
        this.galShow(this.pdpVariant?.image || this.pdpSelectedColor?.image);
      }
    },
    selectSize(v) {
      if (this.variantSoldOut(v)) return;
      this.pdpVariantId = v.id;
      this.pdpQty = 1;
      this.galShow(v.image || this.pdpSelectedColor?.image);
    },
    pdpQtyInput(e) {
      let n = parseInt(String(e.target.value).replace(/\D/g, ''), 10);
      if (isNaN(n) || n < 1) n = 1;
      n = Math.min(n, Math.max(1, this.pdpMax));
      this.pdpQty = n;
      e.target.value = n;
    },
    addFromDetail() {
      const p = this.detailProduct;
      const row = this.pdpRow;
      if (!p || !row || this.pdpMax <= 0) return;
      const qty = Math.min(this.pdpQty, this.pdpMax);
      this.addLine(p, row, qty, (!row.single && row.image) || this.galImageUrl);
      this.pdpQty = 1;
      this.openCart();
      this.fabBump = true;
      setTimeout(() => { this.fabBump = false; }, 600);
    },
    lineKey(product, row) {
      return row.single ? `${product.id}` : `${product.id}_${row.id}`;
    },
    variantLabel(v) {
      const parts = [v.color, v.size].filter(Boolean);
      return parts.length ? parts.join(' / ') : String(v.title || '');
    },
    // row: variante elegida o la fila única de un producto sin variantes
    addLine(product, row, qty, image) {
      const variant = row.single ? null : row;
      const key = this.lineKey(product, row);
      const existing = this.cart.find((l) => l.key === key);
      if (existing) {
        existing.qty = existing.maxQty ? Math.min(existing.qty + qty, existing.maxQty) : existing.qty + qty;
        return;
      }
      const maxQty = variant
        ? (parseInt(variant.inventory_quantity) || 0)
        : (product.manage_stock ? parseInt(product.quantity) || 0 : null);
      // Precio al mayor (de la variante si existe, si no el del producto) con descuento aplicado
      this.cart.push({
        key,
        productId: product.id,
        name: product.name,
        ref: product.ref || '',
        image: image || this.currentSlideUrl(product),
        price: this.finalPrice(product, this.variantUnitPrice(row)),
        discount: this.discountOf(product),
        variantId: variant ? variant.id : null,
        variantLabel: variant ? this.variantLabel(variant) : '',
        qty,
        maxQty,
        raw: product,
      });
    },
    // ===== Drawer =====
    openCart() {
      this.showCart = true;
    },
    closeCart() {
      this.showCart = false;
      this.formError = '';
    },
    lockBody(lock) {
      document.body.style.overflow = lock ? 'hidden' : '';
    },
    changeQty(line, delta) {
      if (delta < 0 && line.qty <= 1) {
        this.removeLine(line);
        return;
      }
      const next = line.qty + delta;
      if (line.maxQty && next > line.maxQty) return;
      line.qty = next;
    },
    lineInput(line, e) {
      const sanitized = String(e.target.value).replace(/\D/g, '');
      let n = parseInt(sanitized, 10);
      if (isNaN(n)) n = 0;
      if (line.maxQty) n = Math.min(n, line.maxQty);
      line.qty = n;
      e.target.value = n;
      if (n === 0) this.removeLine(line);
    },
    removeLine(line) {
      this.cart = this.cart.filter((l) => l.key !== line.key);
    },
    async submitOrder() {
      const missing = [];
      if (!this.form.name.trim()) missing.push('nombre');
      if (!this.form.country.trim()) missing.push('país');
      if (!this.form.city.trim()) missing.push('ciudad');
      if (!this.form.address.trim()) missing.push('dirección');
      if (missing.length) {
        this.formError = `Faltan campos obligatorios: ${missing.join(', ')}`;
        return;
      }
      if (!this.minReached) {
        this.formError = `Te faltan ${MIN_UNITS - this.unitsCount} unidad(es) para llegar al mínimo de ${MIN_UNITS} por pedido.`;
        return;
      }
      this.formError = '';
      this.sending = true;

      const line = '─'.repeat(28);
      let msg = '';
      msg += `*PEDIDO DE CATÁLOGO MAYORISTA*\n`;
      msg += `${line}\n`;
      msg += `Vendedor: ${this.sellerName || 'N/A'}\n`;
      msg += `Fecha: ${new Date().toLocaleString('es-CO')}\n`;
      msg += `${line}\n\n`;
      msg += `*PRODUCTOS*\n`;
      msg += `${line}\n`;
      this.cart.forEach((item, idx) => {
        msg += `${idx + 1}. ${item.name}\n`;
        if (item.variantLabel) msg += `   Talla/Color: ${item.variantLabel}\n`;
        msg += `   Cant: ${item.qty} x ${this.formatCop(item.price)}\n`;
        if (item.discount > 0) msg += `   Descuento aplicado: ${this.formatPct(item.discount)}%\n`;
        msg += `   Subtotal: ${this.formatCop(item.price * item.qty)}\n`;
        if (idx < this.cart.length - 1) msg += `\n`;
      });
      msg += `${line}\n`;
      msg += `*TOTAL: ${this.formatCop(this.subtotal)}*\n`;
      if (this.dollarRate > 0) msg += `Aprox. USD ${this.formatUsd(this.subtotal)}\n`;
      msg += `Envío: se cotiza al momento del despacho\n`;
      msg += `${line}\n\n`;
      msg += `*DATOS DE ENVÍO*\n`;
      msg += `${line}\n`;
      msg += `Nombre: ${this.form.name}\n`;
      msg += `País: ${this.form.country}\n`;
      msg += `Ciudad: ${this.form.city}\n`;
      msg += `Dirección: ${this.form.address}\n`;
      msg += `${line}\n`;
      msg += `¡Gracias! 🛍️`;

      const phone = this.sellerPhone ? this.sellerPhone.replace(/\D/g, '') : '';
      const waNumber = phone.startsWith('57') ? phone : '57' + phone;
      const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;

      window.open(url, '_blank');
      this.sending = false;
      this.cart = [];
      this.closeCart();
      this.form = { name: '', country: 'Colombia', city: '', address: '' };
    },
    // ===== Carga =====
    async loadSellerInfo() {
      try {
        const res = await catalogService.getSellerInfo(this.token);
        this.sellerName = res.data.seller_name || '';
        this.sellerPhone = res.data.seller_phone || '';
        this.companyName = res.data.company_name || '';
        this.dollarRate = parseFloat(res.data.dollar_rate) || 0;
        this.banners = res.data.banners || [];
        this.startBannerTimer();
        const logoPath = res.data.logo_path;
        if (logoPath) {
          this.logoUrl = this.imageUrl(logoPath);
        }
      } catch (e) {
        console.error('Error loading seller info:', e);
      }
    },
    async loadProducts() {
      try {
        const res = await catalogService.getProducts({ per_page: 1000 });
        const imgBase = (process.env.VUE_APP_API_IMAGE_URL || '').replace(/\/?$/, '/');
        this.products = (res.data.data || []).map((p) => ({
          ...p,
          _slide: 0,
          _dead: [],
          photos: (p.photos || []).map((m) => imgBase + String(m.file_path).replace(/^\/+/, '')),
          variants: (p.variants || []).map((v) => ({
            ...v,
            image: v.image ? imgBase + String(v.image).replace(/^\/+/, '') : null,
          })),
        }));
      } catch (e) {
        console.error('Error loading products:', e);
      } finally {
        this.loading = false;
      }
    },
    async loadCategories() {
      try {
        const res = await catalogService.getCategories();
        this.categories = res.data?.data || [];
      } catch (e) {
        console.error('Error loading categories:', e);
        this.categories = [];
      }
      this.$nextTick(this.updateChipsArrows);
    },
    onScroll() {
      this.headerShadow = window.scrollY > 8;
    },
  },
  mounted() {
    this.token = this.$route.params.token;
    if (this.token) {
      this.loadSellerInfo();
    }
    this.loadProducts();
    this.loadCategories();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.updateChipsArrows, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateChipsArrows);
    this.stopBannerTimer();
    document.body.style.overflow = '';
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Assistant:wght@400;500;600;700&display=swap');
</style>

<style scoped>
.cat-page {
  --ink: #121212;
  --ink-75: rgba(18, 18, 18, 0.75);
  --ink-55: rgba(18, 18, 18, 0.55);
  --line: rgba(18, 18, 18, 0.1);
  --brand: #ec91ba;
  --brand-dark: #d478a8;
  --pastel: #f4a7c8;
  --pastel-hover: #ee8fb8;
  --pastel-ink: #5c1f3c;
  --pastel-line: #ec94bb;
  font-family: 'Assistant', 'Segoe UI', system-ui, sans-serif;
  background: #fff;
  min-height: 100vh;
  color: var(--ink);
  padding-bottom: 0;
}
.cat-container {
  max-width: 1280px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
}
@media (min-width: 768px) {
  .cat-container {
    padding-left: 32px;
    padding-right: 32px;
  }
}

/* ============ Barra de anuncios ============ */
.cat-announce {
  background: var(--pastel);
  color: var(--pastel-ink);
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 8px 16px;
}

/* ============ Header ============ */
.cat-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 1px solid var(--line);
  transition: box-shadow 0.2s ease;
}
.cat-header--shadow {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.cat-header-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 10px;
  padding-bottom: 10px;
}
.cat-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.cat-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f6f6;
  color: rgba(18, 18, 18, 0.3);
  font-size: 15px;
}
.cat-brand-name {
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-search-wrap {
  position: relative;
  flex: 1;
  max-width: 420px;
  margin-left: auto;
}
.cat-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: var(--ink-55);
  pointer-events: none;
}
.cat-search {
  width: 100%;
  border: 1px solid var(--pastel-line);
  border-radius: 999px;
  font-family: inherit;
  font-size: 13.5px;
  padding: 9px 34px 9px 36px;
  outline: none;
  background: #fff;
  color: var(--ink);
}
.cat-search:focus {
  border-color: var(--pastel-line);
}
.cat-search-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-55);
  cursor: pointer;
}
.cat-search-clear:hover { background: rgba(18, 18, 18, 0.06); }
.cat-cart-btn {
  position: relative;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 1.05rem;
  cursor: pointer;
  flex-shrink: 0;
}
.cat-cart-btn:hover { background: rgba(18, 18, 18, 0.05); }
.cat-cart-badge {
  position: absolute;
  top: -2px;
  right: -4px;
  background: var(--brand);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  box-shadow: 0 0 0 2px #fff;
}
@media (max-width: 750px) {
  .cat-header-inner { flex-wrap: wrap; }
  .cat-search-wrap {
    order: 3;
    flex-basis: 100%;
    max-width: none;
  }
  .cat-menu-btn { margin-left: auto; }
  .cat-cart-btn { margin-right: -8px; }
}

.cat-menu-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: var(--pastel);
  color: var(--pastel-ink);
  font-size: 1.05rem;
  cursor: pointer;
  flex-shrink: 0;
}
.cat-menu-btn:hover { background: var(--pastel-hover); }

/* ============ Menú de categorías (pantalla completa) ============ */
.catmenu {
  position: fixed;
  inset: 0;
  z-index: 210;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.catmenu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
}
.catmenu-title {
  font-size: 17px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.catmenu-body {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 0 24px;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
}
.catmenu-group-title {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-55);
  padding: 18px 20px 6px;
}
.catmenu-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  border-bottom: 1px solid var(--line);
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  text-align: left;
  padding: 14px 20px;
  cursor: pointer;
}
.catmenu-item:hover { background: rgba(244, 167, 200, 0.15); }
.catmenu-item--active {
  background: var(--pastel);
  color: var(--pastel-ink);
}
.catmenu-item--active:hover { background: var(--pastel); }
.catmenu-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-55);
  flex-shrink: 0;
}
.catmenu-item--active .catmenu-count { color: var(--pastel-ink); }

/* ============ Banners ============ */
.cat-banner {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #f6f6f6;
  touch-action: pan-y;
}
/* Las diapositivas se apilan en la misma celda: el alto lo da la imagen más alta */
.cat-banner-track { display: grid; }
.cat-banner-slide {
  grid-area: 1 / 1;
  align-self: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s ease;
}
.cat-banner-slide--active {
  opacity: 1;
  pointer-events: auto;
}
.cat-banner-slide img {
  width: 100%;
  height: auto;
  display: block;
}

/* ============ Título ============ */
.cat-title {
  text-align: center;
  padding: 26px 0 6px;
}
.cat-title h1 {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #000;
  -webkit-text-fill-color: #000;
}
.cat-title p {
  font-size: 13.5px;
  color: var(--ink-55);
  margin: 0;
}

/* ============ Promociones ============ */
.cat-promos {
  padding: 18px 0 4px;
}
.cat-promos-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 10px;
  color: var(--ink);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.cat-promos-title i {
  color: var(--brand);
  margin-right: 4px;
}
.cat-promos-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.cat-promo {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  border: 1px solid var(--pastel-line);
  border-radius: 12px;
  background: #fff6fa;
  font-family: inherit;
  color: var(--ink);
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
}
.cat-promo:hover {
  box-shadow: 0 2px 10px rgba(236, 145, 186, 0.25);
}
.cat-promo--active {
  background: var(--pastel);
  color: var(--pastel-ink);
}
.cat-promo-pct {
  flex-shrink: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--pastel-ink);
  min-width: 64px;
  text-align: center;
}
.cat-promo-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.cat-promo-name {
  font-size: 15px;
  font-weight: 700;
}
.cat-promo-desc {
  font-size: 13px;
  color: var(--ink-75);
}
.cat-promo-meta {
  font-size: 12px;
  color: var(--ink-55);
}
.cat-promo--active .cat-promo-desc,
.cat-promo--active .cat-promo-meta {
  color: var(--pastel-ink);
}
.cat-promo-cta {
  flex-shrink: 0;
  font-size: 12.5px;
  font-weight: 600;
  border: 1px solid var(--pastel-line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  padding: 6px 14px;
}
@media (min-width: 768px) {
  .cat-promos-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ============ Toolbar ============ */
.cat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0 10px;
}
.cat-count {
  font-size: 13px;
  color: var(--ink-55);
}
.cat-sort { position: relative; }
.cat-sort-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid var(--pastel-line);
  border-radius: 8px;
  background: #fff;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink);
  padding: 8px 32px 8px 12px;
  outline: none;
  cursor: pointer;
}
.cat-sort-chevron {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.65rem;
  color: var(--ink-55);
  pointer-events: none;
}

/* ============ Chips de categorías ============ */
/* Una sola línea con scroll horizontal, de borde a borde del contenedor */
.cat-chips-wrap {
  position: relative;
  margin: 0 -16px 16px;
}
.cat-chips {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0 16px 2px;
}
.cat-chips::-webkit-scrollbar { display: none; }
@media (min-width: 768px) {
  .cat-chips-wrap {
    margin-left: -32px;
    margin-right: -32px;
  }
  .cat-chips {
    padding-left: 32px;
    padding-right: 32px;
  }
}
/* Flecha con degradado: indica que hay más categorías hacia ese lado */
.cat-chips-arrow {
  position: absolute;
  top: 0;
  bottom: 2px;
  z-index: 2;
  width: 56px;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.cat-chips-arrow--prev {
  left: 0;
  justify-content: flex-start;
  padding-left: 8px;
  background: linear-gradient(to right, #fff 45%, rgba(255, 255, 255, 0));
}
.cat-chips-arrow--next {
  right: 0;
  justify-content: flex-end;
  padding-right: 8px;
  background: linear-gradient(to left, #fff 45%, rgba(255, 255, 255, 0));
}
.cat-chips-arrow span {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--pastel-line);
  background: #fff;
  color: var(--pastel-ink);
  font-size: 0.62rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.cat-chips-arrow:hover span { background: var(--pastel); }
.cat-chip {
  flex-shrink: 0;
  white-space: nowrap;
  border: 1px solid var(--pastel-line);
  border-radius: 999px;
  background: #fff;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink);
  padding: 7px 16px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.cat-chip--active {
  background: var(--pastel);
  border-color: var(--pastel-line);
  color: var(--pastel-ink);
}

/* ============ Grilla ============ */
.pgrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px 12px;
  padding-bottom: 40px;
}
@media (min-width: 750px) {
  .pgrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 36px 16px;
  }
}
@media (min-width: 1100px) {
  .pgrid { grid-template-columns: repeat(4, 1fr); }
}

/* ============ Tarjeta ============ */
.pcard { cursor: pointer; }
.pcard-media {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #f6f6f6;
  transition: box-shadow 0.35s ease;
}
.pcard:hover .pcard-media {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
}
.pcard-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform-origin: var(--zx, 50%) var(--zy, 50%);
  transition: transform 0.35s ease;
}
/* Zoom al pasar el mouse (solo en dispositivos con puntero) */
@media (hover: hover) and (pointer: fine) {
  .pcard-media { cursor: zoom-in; }
  .pcard:hover .pcard-media img { transform: scale(1.6); }
}
.slidefade-enter-active,
.slidefade-leave-active {
  transition: opacity 0.25s ease;
}
.slidefade-enter-from,
.slidefade-leave-to {
  opacity: 0;
}
.pcard-ph {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
  color: rgba(18, 18, 18, 0.15);
}
.pcard-badge {
  position: absolute;
  top: 8px;
  z-index: 2;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 5px 10px;
  white-space: nowrap;
}
.pcard-badge--out {
  left: 8px;
  background: #fff;
  color: var(--ink);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.pcard-badge--sale {
  left: 8px;
  background: var(--pastel);
  color: var(--pastel-ink);
}
.pcard-badge--stock {
  right: 8px;
  background: rgba(18, 18, 18, 0.72);
  color: #fff;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.pcard-info {
  padding: 12px 2px 0;
  text-align: left;
}
.pcard-name {
  font-size: 13.5px;
  font-weight: 500;
  margin: 0 0 4px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pcard-price-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.pcard-price {
  font-size: 16px;
  font-weight: 700;
}
.price-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-75);
}
.price-old {
  font-size: 11.5px;
  color: var(--ink-55);
}
.price-retail {
  font-size: 12px;
  color: var(--ink-55);
  margin: 3px 0 0;
}
.price-retail .price-old { font-size: 11px; }
/* P.V. sugerido: etiqueta pequeña + valor al lado */
.price-suggested {
  display: flex;
  align-items: center;
  gap: 8px;
}
.price-suggested-label {
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1.15;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-55);
  padding-right: 8px;
  border-right: 1px solid var(--line);
}
.price-suggested-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-75);
}
/* Modal: bloque de precio — mayor destacado arriba, detal como línea secundaria */
.vprice {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #fffafc;
}
.vprice-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-55);
}
.vprice-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 2px;
}
.vprice-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
}
.vprice-cur {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-55);
}
.vprice-old {
  font-size: 13px;
  color: var(--ink-55);
}
.vprice-row .pill { align-self: center; }
.vprice-usd {
  display: inline-block;
  margin-top: 6px;
}
.vprice-label--stack {
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 0.06em;
}
.vprice-retail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.vprice-main + .vprice-retail {
  border-top: 1px dashed var(--line);
  margin-top: 10px;
  padding-top: 8px;
}
.vprice-retail-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink-75);
}
.vprice-retail-value .vprice-old { font-size: 12px; margin-left: 4px; }
.pill {
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
  line-height: 1.6;
}
.pill-cop {
  background: #eeeef4;
  border: 1px solid #d6d6e0;
  color: #5c5c72;
}
.pill-sale {
  background: #fde8f1;
  border: 1px solid var(--pastel-line);
  color: var(--pastel-ink);
}
.pill-usd {
  background: #e8f5ec;
  border: 1px solid #bfe3cb;
  color: #1a7a3c;
}
.pcard-ref {
  font-size: 11.5px;
  color: var(--ink-55);
  margin: 3px 0 0;
}

/* Carrusel (flechas/dots) */
.pcard-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 0.62rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;
}
.pcard-arrow--prev { left: 8px; }
.pcard-arrow--next { right: 8px; }
.pcard:hover .pcard-arrow { opacity: 1; }
.pcard-arrow:hover { background: var(--pastel); color: var(--pastel-ink); }
@media (hover: none) {
  .pcard-arrow { opacity: 1; }
}
.pcard-dots {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
  z-index: 3;
}
.pcard-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 1px 3px rgba(18, 18, 18, 0.3);
  cursor: pointer;
  transition: background 0.2s, width 0.2s;
}
.pcard-dot--active {
  background: var(--brand-dark);
  width: 14px;
}

/* ============ Skeleton ============ */
.pcard-skeleton { display: flex; flex-direction: column; gap: 8px; }
.pcard-skel-media {
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: #f0f0f0;
  position: relative;
  overflow: hidden;
}
.pcard-skel-line {
  height: 12px;
  border-radius: 6px;
  background: #f0f0f0;
  position: relative;
  overflow: hidden;
}
.pcard-skel-media::after,
.pcard-skel-line::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* ============ Vacío ============ */
.cat-empty {
  text-align: center;
  padding: 70px 20px;
  color: var(--ink-55);
}
.cat-empty i { font-size: 40px; opacity: 0.35; margin-bottom: 14px; }
.cat-empty p { font-size: 14px; margin: 0 0 8px; }
.cat-empty a {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}

/* ============ Footer ============ */
.cat-footer {
  border-top: 1px solid var(--line);
  text-align: center;
  padding: 30px 16px 26px;
}
.cat-footer-logo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--line);
  object-fit: cover;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f6f6f6;
  color: rgba(18, 18, 18, 0.3);
}
.cat-footer-brand {
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin: 10px 0 2px;
}
.cat-footer-tag {
  font-size: 12.5px;
  color: var(--ink-55);
  margin: 0 0 6px;
}
.cat-footer-copy {
  font-size: 11.5px;
  color: var(--ink-55);
  margin: 0;
}

/* ============ Modal de variantes ============ */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.vmodal-close {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 0.95rem;
  cursor: pointer;
  flex-shrink: 0;
}
.vmodal-close:hover { background: rgba(18, 18, 18, 0.06); }
/* Galería del detalle de producto */
.pgal {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  max-height: 52vh;
  background: #f6f6f6;
  overflow: hidden;
  touch-action: pan-y;
}
.pgal img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transform-origin: var(--zx, 50%) var(--zy, 50%);
  transition: transform 0.25s ease;
}
@media (hover: hover) and (pointer: fine) {
  .pgal { cursor: zoom-in; }
  .pgal:hover img { transform: scale(2.2); }
}
.pgal--zoom { touch-action: none; }
.pgal--zoom img { transform: scale(2.2); }
.pgal-zoom {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink);
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
}
.pgal-zoom:hover,
.pgal--zoom .pgal-zoom { background: var(--pastel); color: var(--pastel-ink); }
.pgal-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink);
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
}
.pgal-arrow--prev { left: 10px; }
.pgal-arrow--next { right: 10px; }
.pgal-arrow:hover { background: var(--pastel); color: var(--pastel-ink); }
.pgal-counter {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(18, 18, 18, 0.65);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 3px 9px;
}
.pgal-thumbs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 10px 20px 0;
}
.pgal-thumbs::-webkit-scrollbar { display: none; }
.pgal-thumb {
  flex-shrink: 0;
  width: 48px;
  height: 60px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #f6f6f6;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s, border-color 0.15s;
}
.pgal-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pgal-thumb--active {
  border-color: var(--brand-dark);
  opacity: 1;
}
.stepper {
  display: flex;
  align-items: center;
  border: 1px solid var(--pastel-line);
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}
.stepper-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  color: var(--ink);
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
}
.stepper-btn:disabled { color: rgba(18, 18, 18, 0.25); cursor: default; }
.stepper-input {
  width: 42px;
  border: none;
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
  text-align: center;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  padding: 6px 0;
  outline: none;
}
.btn--dark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--pastel);
  color: var(--pastel-ink);
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 14px 22px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.btn--dark:hover { background: var(--pastel-hover); }
.btn--dark:disabled {
  background: #e5e5e5;
  color: #9a9a9a;
  cursor: default;
}

/* ============ Detalle de producto ============ */
.cat-brand--link { cursor: pointer; }
.pdp { padding-top: 14px; padding-bottom: 48px; }
.pdp-nav {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.pdp-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--pastel-line);
  border-radius: 999px;
  background: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.pdp-back:hover { background: rgba(244, 167, 200, 0.18); }
.pdp-crumb {
  font-size: 12.5px;
  color: var(--ink-55);
}
.pdp-loading {
  text-align: center;
  padding: 80px 0;
  font-size: 26px;
  color: var(--ink-55);
}
.pdp-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
}
.pdp-media { min-width: 0; }
.pdp .pgal {
  max-height: none;
  border-radius: 12px;
}
.pdp .pcard-badge { left: 12px; top: 12px; }
.pdp .pgal-thumbs { padding: 10px 0 0; }
.pdp .pgal-thumb { width: 64px; height: 80px; }
.pdp-info { min-width: 0; }
.pdp-ref {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-55);
  margin: 0 0 4px;
}
.pdp-title {
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 14px;
  color: #000;
  -webkit-text-fill-color: #000;
}
.pdp-opt { margin-top: 20px; }
.pdp-opt-label {
  font-size: 13px;
  color: var(--ink-75);
  margin-bottom: 8px;
}
.pdp-opt-label strong { color: var(--ink); }
.pdp-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pdp-swatch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(18, 18, 18, 0.15);
  border-radius: 999px;
  background: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  padding: 4px 14px 4px 4px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.pdp-swatch:hover { border-color: var(--pastel-line); }
.pdp-swatch--text { padding: 9px 16px; }
.pdp-swatch--active {
  border-color: var(--ink);
  box-shadow: 0 0 0 1px var(--ink);
}
.pdp-swatch--out .pdp-swatch-name {
  color: var(--ink-55);
  text-decoration: line-through;
}
.pdp-swatch-dot,
.pdp-swatch-img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--line);
  overflow: hidden;
  display: block;
}
.pdp-swatch-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pdp-sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pdp-size {
  min-width: 52px;
  height: 44px;
  border: 1px solid rgba(18, 18, 18, 0.15);
  border-radius: 8px;
  background: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  padding: 0 14px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-size:hover:not(:disabled) { border-color: var(--pastel-line); }
.pdp-size--active {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}
.pdp-size--out {
  color: rgba(18, 18, 18, 0.3);
  background: repeating-linear-gradient(135deg, #fff 0 6px, #f4f4f4 6px 7px);
  text-decoration: line-through;
  cursor: not-allowed;
}
.pdp-hint {
  font-size: 12.5px;
  color: var(--ink-55);
  margin: 8px 0 0;
}
.pdp-buy {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}
.pdp-stock {
  font-size: 13px;
  font-weight: 600;
  color: #1a7f4b;
  margin: 0 0 10px;
}
.pdp-incart { color: var(--ink-55); font-weight: 500; }
.pdp-buy-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}
.pdp-buy-row .stepper-btn { height: 48px; width: 40px; }
.pdp-add { flex: 1; }
.pdp-section {
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}
.pdp-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 10px;
  color: var(--ink);
}
.pdp-desc {
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ink-75);
  white-space: pre-line;
  margin: 0;
}
.pdp-specs {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 16px;
  font-size: 14px;
  margin: 0;
}
.pdp-specs dt { color: var(--ink-55); font-weight: 500; }
.pdp-specs dd { margin: 0; color: var(--ink); }
@media (min-width: 900px) {
  .pdp-grid {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 48px;
    align-items: start;
  }
  .pdp-title { font-size: 30px; }
}

/* ============ Drawer del carrito ============ */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.45);
  z-index: 200;
}
.drawer {
  position: fixed;
  right: 0;
  top: 0;
  height: 100dvh;
  width: 100%;
  max-width: 430px;
  background: #fff;
  z-index: 201;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  animation: drawerSlide 0.28s ease;
}
@keyframes drawerSlide {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
}
.drawer-title {
  font-size: 17px;
  font-weight: 600;
}
.drawer-count {
  font-size: 14px;
  color: var(--ink-55);
}
.drawer-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
}
.drawer-empty i { font-size: 34px; opacity: 0.4; }
.drawer-empty p { font-size: 14px; color: var(--ink-55); margin: 0 0 6px; }
.drawer-lines {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px;
}
.dline {
  display: flex;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(18, 18, 18, 0.06);
}
.dline-thumb {
  width: 68px;
  height: 88px;
  border-radius: 10px;
  background: #f6f6f6;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(18, 18, 18, 0.25);
}
.dline-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dline-info { flex: 1; min-width: 0; }
.dline-name {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 4px;
}
.dline-variant {
  display: inline-block;
  background: #f4f4f4;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 9px;
  margin-bottom: 4px;
}
.dline-unit {
  font-size: 11.5px;
  color: var(--ink-55);
  margin: 0 0 6px;
}
.dline-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}
.stepper--sm .stepper-btn { width: 24px; height: 24px; font-size: 0.75rem; }
.stepper--sm .stepper-input { width: 28px; font-size: 12px; padding: 4px 0; }
.dline-remove {
  font-size: 11.5px;
  color: var(--ink-55);
  text-decoration: underline;
}
.dline-remove:hover { color: #b3261e; }
.dline-total {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.drawer-foot {
  border-top: 1px solid var(--line);
  padding: 16px 20px 20px;
  max-height: 62vh;
  overflow-y: auto;
}
.minbar { margin-bottom: 12px; }
.minbar-track {
  height: 3px;
  background: #eee;
  border-radius: 999px;
  overflow: hidden;
}
.minbar-fill {
  height: 100%;
  background: var(--brand);
  border-radius: 999px;
  transition: width 0.25s ease;
}
.minbar-text {
  font-size: 10.5px;
  color: var(--ink-55);
  margin: 5px 0 0;
}
.minbar-text--ok { color: #1a7f4b; }
.drawer-subtotal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.drawer-subtotal-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--ink-55);
}
.drawer-subtotal-value {
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
}
.drawer-shipping {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.drawer-shipping-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-75);
}
.drawer-note {
  font-size: 11.5px;
  color: var(--ink-55);
  margin: 0 0 4px;
}
.drawer-note i {
  width: 16px;
  text-align: center;
  margin-right: 2px;
}
.drawer-note + .drawer-form { margin-top: 12px; }
.drawer-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field {
  width: 100%;
  font-family: inherit;
  font-size: 13px;
  color: var(--ink);
  border: 1px solid var(--pastel-line);
  border-radius: 8px;
  padding: 10px 12px;
  outline: none;
  background: #fff;
}
.field:focus { border-color: var(--pastel-line); }
.field:disabled { background: #f7f7f7; }
.drawer-error {
  font-size: 12px;
  color: #b3261e;
  margin: 0;
}
.btn-wa {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--pastel);
  color: var(--pastel-ink);
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 14px 22px;
  cursor: pointer;
}
.btn-wa:hover { background: var(--pastel-hover); }
.btn-wa:disabled { opacity: 0.6; cursor: default; }

/* ============ Botón flotante del carrito ============ */
.cat-fab {
  position: fixed;
  right: 18px;
  bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  z-index: 150;
  width: 58px;
  height: 58px;
  border: none;
  border-radius: 50%;
  background: var(--pastel);
  color: var(--pastel-ink);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(92, 31, 60, 0.25);
  transition: background 0.2s ease, transform 0.2s ease;
}
.cat-fab:hover { background: var(--pastel-hover); }
.cat-fab--bump { animation: fabBump 0.6s ease; }
.cat-fab-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--ink);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}
@keyframes fabBump {
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.15); }
}
.fab-enter-active, .fab-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fab-enter-from, .fab-leave-to { opacity: 0; transform: scale(0.6); }
</style>
